import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { SetDetail } from 'store/services/trade/tradeApi';
import { HaveFinish, PriceKey } from 'utils/finishes';
import { sumRealPrices } from 'utils/price';

export interface SidebarTradeCard {
  id: string;                    // Unique line item ID
  wantListId: number;            // Want list entry reference
  cardId?: number;               // Specific printing ID
  cardName: string;
  set: string;
  setCode: string;
  condition: string;
  language: string;
  acceptableLanguages?: string[];  // Backend lowercase codes from want list (e.g. ['es'])
  /** Derived mirror of `finish` (finish !== 'NONFOIL'); kept in sync by finishFields() */
  foil: boolean;
  /** Authoritative finish of the staged copy (Track D) */
  finish: HaveFinish;
  imageUrl: string;
  /** Null when the staged printing has no price; read it through hasRealPrice (a restored 0 is no price too). */
  pointValue: number | null;
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  acceptEtched?: boolean | null;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  /** Finishes the printing exists in (lowercase); the three-value select reads this */
  availableFinishes?: string[];
  etchedPointValue?: number | null;
  scryfallId?: string;
  /** Every language this printing exists in; `id` is that variant's MTGCard id (want-list rows and GET /cards/:scryfallId). */
  availableLanguages?: Array<{ id?: number; lang: string; scryfallId: string; setCode?: string }>;
  isInvalid?: boolean;           // Error state flag
  maxQuantity: number;           // Original quantity from want list
  languageMultiplierApplied?: boolean;
  multiplierValue?: number;
  /** The Price column pointValue resolved through (backend Track A); drives the estimate marker */
  priceKey?: PriceKey | null;
}

interface ProfileTradeSidebarContextType {
  // State
  cards: SidebarTradeCard[];
  isOpen: boolean;
  receiverUsername: string | null;
  receiverId: number | null;

  // Computed
  totalCards: number;
  totalPoints: number;

  // Actions
  addCard: (card: Omit<SidebarTradeCard, 'id'>) => void;
  removeCard: (id: string) => void;
  updateCard: (id: string, updates: Partial<SidebarTradeCard>) => void;
  clearAll: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  setReceiver: (username: string, id: number) => void;
  getAddedCountForWantListId: (wantListId: number) => number;
}

const ProfileTradeSidebarContext = createContext<ProfileTradeSidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'profile_trade_sidebar';

/**
 * Persisted-session schema version. The sidebar had no version before Track D;
 * v1 adds `finish` on every staged card. An unversioned session carries only
 * `foil`, so an etched copy would be restored as FOIL: it is discarded rather
 * than restored (the sidebar simply starts empty).
 */
const STORAGE_SCHEMA_VERSION = 1;

interface StorageData {
  version?: number;
  cards: SidebarTradeCard[];
  receiverUsername: string | null;
  receiverId: number | null;
}

export const ProfileTradeSidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cards, setCards] = useState<SidebarTradeCard[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [receiverUsername, setReceiverUsername] = useState<string | null>(null);
  const [receiverId, setReceiverId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from session storage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: StorageData = JSON.parse(stored);
        if (data.version !== STORAGE_SCHEMA_VERSION) {
          // Pre-Track-D session (no finish on the cards): discard, never restore
          sessionStorage.removeItem(STORAGE_KEY);
          setIsInitialized(true);
          return;
        }
        setCards(data.cards || []);
        setReceiverUsername(data.receiverUsername);
        setReceiverId(data.receiverId);
        // Open sidebar if there are cards
        if (data.cards && data.cards.length > 0) {
          setIsOpen(true);
        }
      }
    } catch (e) {
      console.error('Failed to load trade sidebar from storage:', e);
    }
    setIsInitialized(true);
  }, []);

  // Save to session storage when state changes
  useEffect(() => {
    if (!isInitialized) return;

    try {
      const data: StorageData = {
        version: STORAGE_SCHEMA_VERSION,
        cards,
        receiverUsername,
        receiverId,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save trade sidebar to storage:', e);
    }
  }, [cards, receiverUsername, receiverId, isInitialized]);

  const totalCards = useMemo(() => cards.length, [cards]);
  const totalPoints = useMemo(() =>
    // A no-price card adds nothing; it also blocks Create Trade (see the sidebar)
    sumRealPrices(cards.map((card) => card.pointValue)),
    [cards]
  );

  const addCard = useCallback((card: Omit<SidebarTradeCard, 'id'>) => {
    const id = `${card.wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCards(prev => [...prev, { ...card, id }]);
    setIsOpen(true);
  }, []);

  const removeCard = useCallback((id: string) => {
    setCards(prev => prev.filter(card => card.id !== id));
  }, []);

  const updateCard = useCallback((id: string, updates: Partial<SidebarTradeCard>) => {
    setCards(prev => prev.map(card =>
      card.id === id ? { ...card, ...updates } : card
    ));
  }, []);

  const clearAll = useCallback(() => {
    setCards([]);
    setIsOpen(false);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const openSidebar = useCallback(() => setIsOpen(true), []);
  const closeSidebar = useCallback(() => setIsOpen(false), []);

  const setReceiver = useCallback((username: string, id: number) => {
    // If switching to a different user, clear the cart
    if (receiverId !== null && receiverId !== id) {
      setCards([]);
    }
    setReceiverUsername(username);
    setReceiverId(id);
  }, [receiverId]);

  const getAddedCountForWantListId = useCallback((wantListId: number) => {
    return cards.filter(card => card.wantListId === wantListId).length;
  }, [cards]);

  const value: ProfileTradeSidebarContextType = {
    cards,
    isOpen,
    receiverUsername,
    receiverId,
    totalCards,
    totalPoints,
    addCard,
    removeCard,
    updateCard,
    clearAll,
    openSidebar,
    closeSidebar,
    setReceiver,
    getAddedCountForWantListId,
  };

  return (
    <ProfileTradeSidebarContext.Provider value={value}>
      {children}
    </ProfileTradeSidebarContext.Provider>
  );
};

export const useProfileTradeSidebar = (): ProfileTradeSidebarContextType => {
  const context = useContext(ProfileTradeSidebarContext);
  if (!context) {
    throw new Error('useProfileTradeSidebar must be used within a ProfileTradeSidebarProvider');
  }
  return context;
};

export default ProfileTradeSidebarContext;
